import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Transaction } from '@/types';
import { Loader2, Mail, Send } from 'lucide-react';

interface EmailReceiptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transaction: Transaction;
  client?: {
    name: string;
    email?: string;
    phone?: string;
  };
  receiptNumber: string;
  issueDate: Date;
  companyName: string;
  companyLogo?: string;
}

export function EmailReceiptDialog({
  open,
  onOpenChange,
  transaction,
  client,
  receiptNumber,
  issueDate,
  companyName,
  companyLogo
}: EmailReceiptDialogProps) {
  const [emailTo, setEmailTo] = useState(client?.email || '');
  const [subject, setSubject] = useState(`Recibo ${receiptNumber} - ${companyName}`);
  const [message, setMessage] = useState(
    `Prezado(a) ${client?.name || 'Cliente'},\n\nSegue em anexo o recibo referente ao pagamento efetuado.\n\nValor: R$ ${transaction.amount.toFixed(2).replace('.', ',')}\nData: ${new Date(transaction.payment_date || transaction.date).toLocaleDateString('pt-BR')}\n\nAtenciosamente,\n${companyName}`
  );
  const [isSending, setIsSending] = useState(false);
  const [sendWithPDF, setSendWithPDF] = useState(true);

  // Handle sending email
  const handleSendEmail = async () => {
    if (!emailTo) {
      alert('Por favor, informe o e-mail do destinatário.');
      return;
    }

    setIsSending(true);
    try {
      // Import email service dynamically to avoid SSR issues
      const { sendReceiptEmail } = await import('@/services/receiptService');

      await sendReceiptEmail({
        to: emailTo,
        subject,
        message,
        transaction,
        client,
        receiptNumber,
        issueDate,
        companyName,
        companyLogo,
        includePDF: sendWithPDF
      });

      alert('Recibo enviado com sucesso!');
      onOpenChange(false);
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Erro ao enviar o recibo. Tente novamente.');
    } finally {
      setIsSending(false);
    }
  };

  // Format currency for display
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Enviar Recibo por E-mail
          </DialogTitle>
          <DialogDescription>
            Envie o recibo da transação para o e-mail do cliente
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Email To */}
          <div className="space-y-2">
            <Label htmlFor="email-to">Para</Label>
            <Input
              id="email-to"
              type="email"
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              placeholder="cliente@exemplo.com"
              disabled={isSending}
            />
          </div>

          {/* Subject */}
          <div className="space-y-2">
            <Label htmlFor="subject">Assunto</Label>
            <Input
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={isSending}
            />
          </div>

          {/* Message */}
          <div className="space-y-2">
            <Label htmlFor="message">Mensagem</Label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              disabled={isSending}
            />
          </div>

          {/* Options */}
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="send-pdf"
                checked={sendWithPDF}
                onChange={(e) => setSendWithPDF(e.target.checked)}
                className="rounded"
                disabled={isSending}
              />
              <Label htmlFor="send-pdf" className="text-sm">
                Anexar recibo em PDF
              </Label>
            </div>
          </div>

          {/* Transaction Summary */}
          <div className="bg-muted p-3 rounded-lg space-y-1">
            <p className="text-sm font-medium">Resumo da Transação:</p>
            <p className="text-xs text-muted-foreground">
              {transaction.description}
            </p>
            <p className="text-sm font-semibold text-terracotta-600">
              {formatCurrency(transaction.amount)}
            </p>
            <p className="text-xs text-muted-foreground">
              {transaction.payment_date
                ? `Pago em: ${new Date(transaction.payment_date).toLocaleDateString('pt-BR')}`
                : `Data: ${new Date(transaction.date).toLocaleDateString('pt-BR')}`
              }
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-4">
            <Button
              onClick={() => onOpenChange(false)}
              variant="outline"
              disabled={isSending}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSendEmail}
              disabled={isSending || !emailTo}
              className="flex-1 flex items-center gap-2"
            >
              {isSending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              {isSending ? 'Enviando...' : 'Enviar'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}